// ============================================================
// SCENE - solids in, a drawing order out.
//
// The renderer is deliberately dumb: it maps this list to <polygon>
// elements and nothing else. All the judgement - which faces are worth
// drawing, in what order, how bright - happens here, where it is pure
// and can be tested without a DOM.
//
// DEPTH SORTING IS APPROXIMATE, and honestly so. The painter's algorithm
// cannot be made exact without a depth buffer. Three things keep it
// good enough:
//
//   1. sorting per FACE rather than per solid, which is what handles a
//      long wall passing beside a crate;
//   2. painted terrain arriving pre-segmented into tiles, so there are
//      no long thin spans - the classic failure case;
//   3. the Tactical preset (pitch 90), where no face can occlude another
//      and the result is exact. One click away when something looks odd.
//
// Pure. Zero React dependency.
// ============================================================
import type { FaceKind, Solid, Vec3 } from './types';
import type { Camera, Viewport } from './projection';
import { clipToEye, depthKey, eyePosition, project } from './projection';
import { solidGeometry, centroid, dot, solidTransparency, sub } from './solids';
import { faceShade, terrainTint } from './shading';
import type { SightOptions } from './lineOfSight';

const DEG = Math.PI / 180;

interface ScenePoint {
  x: number;
  y: number;
}

export interface SceneFace {
  /** Stable across rebuilds: same solid, same face, same key. */
  key: string;
  solidId: string;
  kind: FaceKind;
  points: ScenePoint[];
  /**
   * Each corner's own depth, in step with `points`. The display's see-through
   * test needs the depth of the face AT a pixel (a roof is nearer at its front
   * edge than at its back), which one centroid depth cannot give. As
   * `depthKey` gives it: linear across the screen under either camera.
   */
  pointDepths: number[];
  /** Larger is further from the viewer. The list is sorted by this, descending. */
  depth: number;
  /**
   * The face's own height in the world, in metres.
   *
   * Screen space cannot answer this: from directly above, a rooftop and
   * the floor beneath it project to the same place. The `rise` arrival
   * orders by it, and it costs nothing - the centroid it comes from is
   * already computed for the depth sort.
   */
  elevation: number;
  shade: number;
  tint: string | null;
  /** The material's own colour. Absent means the neutral grey. */
  colour?: string;
  /** 0 opaque · 1 translucent · 2 transparent - the renderer's alpha. */
  transparency: 0 | 1 | 2;
  label?: string;
  /**
   * Dangerous ground, and therefore worth a mark of its own.
   *
   * A hazard changes nothing mechanically - it is a warning to the table.
   * A tint alone was not enough of one: it competes with every material
   * colour on the map, and a GM who paints burning promethium orange has
   * just made it invisible as a hazard. The renderer hatches these.
   */
  hazard?: true;
  /**
   * Ground that simply stops you.
   *
   * Marked for the same reason as a hazard: the movement overlay SKIPS
   * impassable ground, so it read as ordinary ground nobody had walked
   * to yet - the opposite of what it means.
   */
  blocked?: true;
  /** Painted ground's motion (zones.ts `surfaceMotion`), for the display; absent is still. */
  motion?: Solid['motion'];
  /**
   * Where the solid's base centre lands on screen.
   *
   * A face's own screen box is no guide to where the solid STANDS: a
   * wall's side face is three metres up, so the ground beneath its
   * projected box is well behind the wall. Anything that needs to reason
   * about a solid's position on the ground plane - a tooltip, a hit
   * test, a measurement aimed at it - wants this instead.
   */
  anchor: ScenePoint;
}

/**
 * Unit vector pointing INTO the screen, away from the viewer.
 *
 * Derived from the projection rather than guessed: the camera stands on
 * the +`towards` side (see `project`), so looking into the scene means
 * moving against it and downwards by the pitch.
 */
export function viewDirection(camera: Pick<Camera, 'yaw' | 'pitch'>): Vec3 {
  const t = camera.yaw * DEG;
  const f = camera.pitch * DEG;
  return {
    x: -Math.sin(t) * Math.cos(f),
    y: -Math.sin(f),
    z: -Math.cos(t) * Math.cos(f),
  };
}

/**
 * The line of sight to a point, pointing into the screen: the one
 * `viewDirection` everywhere under the orthographic camera; from the eye
 * under perspective, where a face out to the side may turn to the eye while
 * it is edge-on to the axis (the review of Track A).
 */
export function sightTo(camera: Camera, view: Viewport): (p: Vec3) => Vec3 {
  const eye = eyePosition(camera, view);
  if (!eye) {
    const direction = viewDirection(camera);
    return () => direction;
  }
  return (p) => sub(p, eye);
}

/**
 * Is this face turned towards the viewer?
 *
 * Exact for convex solids, which is all this map contains - so back-face
 * culling here is not an optimisation that risks correctness, it is the
 * correct thing to do, and it halves the polygon count for free.
 */
export function isFrontFacing(normal: Vec3, direction: Vec3): boolean {
  // The epsilon is not decoration. At a pitch of exactly 90° the cosine
  // is 6e-17 rather than 0, so a wall seen perfectly edge-on scores a
  // dot product of ±1e-17 and half of them would sneak through as
  // zero-width slivers over the top-down map.
  return dot(normal, direction) < -1e-9;
}

export interface SceneOptions extends SightOptions {
  /** Pixels of slack around the viewport before a solid is culled. */
  margin?: number;
}

/** Project a solid's bounding corners and ask whether any of it lands on screen. */
function onScreen(vertices: Vec3[], camera: Camera, view: Viewport, margin: number): boolean {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const v of vertices) {
    const p = project(v, camera, view);
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  return maxX >= -margin && minX <= view.width + margin && maxY >= -margin && minY <= view.height + margin;
}

/**
 * Everything to draw, furthest first.
 *
 * The caller renders the list in order and lets later polygons paint over
 * earlier ones - no z-index, no grouping, no second pass.
 */
export function buildScene(solids: Solid[], camera: Camera, view: Viewport, options: SceneOptions = {}): SceneFace[] {
  const includeHidden = options.includeHidden !== false;
  const margin = options.margin ?? 64;
  const lens: Lens = { camera, view, sight: sightTo(camera, view) };
  const out: SceneFace[] = [];

  for (const solid of solids) {
    if (solid.hidden && !includeHidden) continue;
    const geometry = solidGeometry(solid);
    if (onScreen(geometry.vertices, camera, view, margin)) out.push(...solidFaces(solid, geometry, lens));
  }

  out.sort(furthestFirst);
  return out;
}

/** How the scene is being looked at. */
interface Lens {
  camera: Camera;
  view: Viewport;
  sight: (p: Vec3) => Vec3;
}

/** The faces of one solid that are turned towards the viewer. */
function solidFaces(solid: Solid, geometry: ReturnType<typeof solidGeometry>, lens: Lens): SceneFace[] {
  const looks = solidLooks(solid, lens);
  const faces: SceneFace[] = [];
  geometry.faces.forEach((face, index) => {
    const world = face.indices.map((i) => geometry.vertices[i]);
    if (!isFrontFacing(face.normal, lens.sight(world[0]))) return;
    // Under perspective, only what is in front of the eye is drawn.
    const drawn = clipToEye(world, lens.camera, lens.view);
    if (drawn.length < 3) return;
    // ONE centroid, two questions: how far away it is, and how high.
    const middle = centroid(world);
    const projected = drawn.map((v) => project(v, lens.camera, lens.view));
    faces.push({
      key: `${solid.id}:${index}`,
      solidId: solid.id,
      kind: face.kind,
      points: projected.map((p) => ({ x: p.x, y: p.y })),
      // The key a flat face interpolates linearly across the screen (exact under perspective too).
      pointDepths: projected.map(depthKey),
      depth: project(middle, lens.camera, lens.view).depth,
      elevation: middle.y,
      shade: faceShade(face.normal),
      ...looks,
    });
  });
  return faces;
}

/** What every face of one solid shares: its tint and alpha, its marks, and where it stands. */
function solidLooks(solid: Solid, lens: Lens) {
  const ground = project({ x: solid.position.x, y: 0, z: solid.position.z }, lens.camera, lens.view);
  return {
    tint: terrainTint(solid.terrain),
    transparency: solidTransparency(solid),
    ...(solid.colour ? { colour: solid.colour } : {}),
    anchor: { x: ground.x, y: ground.y },
    ...(solid.label ? { label: solid.label } : {}),
    ...(solid.terrain === 'dangerous' ? { hazard: true as const } : {}),
    ...(solid.terrain === 'impassable' ? { blocked: true as const } : {}),
    ...(solid.motion ? { motion: solid.motion } : {}),
  };
}

/**
 * Furthest first. The tie-break on `key` keeps the order stable between
 * rebuilds, so coincident faces do not flicker as the camera moves.
 */
function furthestFirst(a: SceneFace, b: SceneFace): number {
  const byDepth = b.depth - a.depth;
  if (byDepth) return byDepth;
  if (a.key === b.key) return 0;
  return a.key < b.key ? -1 : 1;
}
